import {
  Box,
  MenuItem,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { memo, useCallback, type ChangeEvent } from "react";
import { ApiAddressLocationType } from "../../../generated/graphql";
import { type AddressFormState } from "../api-order-form";

const styles = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 2,
  } satisfies SxProps<Theme>,
};

const LOCATION_TYPES = Object.values(ApiAddressLocationType);

/** Writes one field of the address, leaving the rest of it untouched. */
type SetAddressField = <TKey extends keyof AddressFormState>(
  key: TKey,
  next: AddressFormState[TKey],
) => void;

type AddressFieldsProps = {
  readonly value: AddressFormState;
  readonly onChange: (next: AddressFormState) => void;
};

/**
 * The address inputs of one stop. Every field except line 2 and the location
 * type is required by the external API, so all of them are marked required
 * here rather than failing server-side validation.
 *
 * Memoized because the API-order form renders two of these, one per stop, and
 * re-renders on every keystroke anywhere in the form.
 */
const AddressFields = memo(({ value, onChange }: AddressFieldsProps) => {
  const set = useCallback<SetAddressField>(
    (key, next) => {
      onChange({ ...value, [key]: next });
    },
    [onChange, value],
  );

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set("name", event.target.value);
    },
    [set],
  );

  const handleLine1Change = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set("line1", event.target.value);
    },
    [set],
  );

  const handleLine2Change = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set("line2", event.target.value);
    },
    [set],
  );

  const handleCityChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set("city", event.target.value);
    },
    [set],
  );

  const handleStateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set("state", event.target.value);
    },
    [set],
  );

  const handleZipChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set("zip", event.target.value);
    },
    [set],
  );

  const handleCountryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set("country", event.target.value);
    },
    [set],
  );

  const handleLocationTypeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set(
        "locationType",
        event.target.value as AddressFormState["locationType"],
      );
    },
    [set],
  );

  return (
    <Box sx={styles.grid}>
      <TextField
        required
        label="Name"
        size="small"
        value={value.name}
        onChange={handleNameChange}
      />
      <TextField
        required
        label="Line 1"
        size="small"
        value={value.line1}
        onChange={handleLine1Change}
      />
      <TextField
        label="Line 2"
        size="small"
        value={value.line2}
        onChange={handleLine2Change}
      />
      <TextField
        required
        label="City"
        size="small"
        value={value.city}
        onChange={handleCityChange}
      />
      <TextField
        required
        label="State"
        size="small"
        value={value.state}
        onChange={handleStateChange}
      />
      <TextField
        required
        label="ZIP"
        size="small"
        value={value.zip}
        onChange={handleZipChange}
      />
      <TextField
        required
        label="Country"
        size="small"
        value={value.country}
        onChange={handleCountryChange}
      />
      <TextField
        select
        label="Location type"
        size="small"
        value={value.locationType}
        onChange={handleLocationTypeChange}
      >
        <MenuItem value="">Unset</MenuItem>
        {LOCATION_TYPES.map((locationType) => (
          <MenuItem key={locationType} value={locationType}>
            {locationType}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
});

AddressFields.displayName = "AddressFields";

export default AddressFields;
